/**
 * DemoRequestModal — landing-page "Schedule a demo" capture. A prospect enters
 * their email (+ optional name/company) and we email them the interactive demo
 * link + passcode via POST /api/demo/request-link.
 */
import React, { useState } from 'react';
import axios from 'axios';
import { X, Loader2, CheckCircle2, Mail } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const DemoRequestModal = ({
  open,
  onClose,
  title = 'Get your interactive demo',
  subtitle = 'See the full product with realistic sample data.',
  submitLabel = 'Email me the demo link',
  successTitle = 'Check your inbox',
  footnote = "No credit card. We'll send a private link + passcode to explore the demo.",
  endpoint = '/demo/request-link',
  companyLabel = 'Company',
}) => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [company, setCompany] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  if (!open) return null;

  const close = () => {
    setEmail(''); setName(''); setCompany('');
    setSent(false); setError(''); setSending(false);
    onClose?.();
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    setError('');
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim())) {
      setError('Please enter a valid email address.');
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}${endpoint}`, {
        email: email.trim(),
        name: name.trim() || null,
        company: company.trim() || null,
      });
      setSent(true);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Something went wrong — please try again.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/70 backdrop-blur-sm p-4"
      onClick={close}
      data-testid="demo-request-overlay"
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        data-testid="demo-request-modal"
      >
        <div className="bg-[#0b3b6f] px-6 py-5 flex items-center justify-between">
          <div>
            <h2 className="text-white text-lg font-bold">{title}</h2>
            <p className="text-blue-200 text-xs mt-0.5">{subtitle}</p>
          </div>
          <button onClick={close} className="text-blue-200 hover:text-white" data-testid="demo-request-close" aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {sent ? (
          <div className="p-8 text-center" data-testid="demo-request-success">
            <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
            <h3 className="text-lg font-bold text-slate-900">{successTitle}</h3>
            <p className="text-sm text-slate-600 mt-1">
              We just emailed the demo link and passcode to <strong>{email.trim()}</strong>.
            </p>
            <button
              onClick={close}
              className="mt-5 inline-flex items-center justify-center px-5 py-2.5 rounded-lg bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800"
              data-testid="demo-request-done"
            >
              Done
            </button>
          </div>
        ) : (
          <form className="p-6 space-y-4" onSubmit={submit} noValidate>
            <div>
              <label className="text-[11px] font-bold uppercase tracking-wider text-slate-600">Work email *</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                className="w-full h-11 px-3 mt-1 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                data-testid="demo-request-email"
                autoFocus
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[11px] font-bold uppercase tracking-wider text-slate-600">Name</label>
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Jane Smith"
                  className="w-full h-11 px-3 mt-1 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  data-testid="demo-request-name"
                />
              </div>
              <div>
                <label className="text-[11px] font-bold uppercase tracking-wider text-slate-600">{companyLabel}</label>
                <input
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                  placeholder="First National"
                  className="w-full h-11 px-3 mt-1 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  data-testid="demo-request-company"
                />
              </div>
            </div>
            {error && <p className="text-sm text-red-600" data-testid="demo-request-error">{error}</p>}
            <button
              type="submit"
              disabled={sending}
              className="w-full h-11 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-semibold flex items-center justify-center gap-2 disabled:opacity-60"
              data-testid="demo-request-submit"
            >
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
              {submitLabel}
            </button>
            <p className="text-[11px] text-slate-400 text-center">
              {footnote}
            </p>
          </form>
        )}
      </div>
    </div>
  );
};

export default DemoRequestModal;
